import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api, { authApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkLoggedIn = useCallback(async () => {
    try {
      const response = await authApi.getMe();
      setUser(response.data);
    } catch (error) {
      // User is not logged in
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    checkLoggedIn();
  }, [checkLoggedIn]);

  // Resume a checkout that was started before the user logged in. When an
  // unauthenticated user clicks a pricing plan we stash the variant and send them
  // through Google login; once they're back and authenticated, continue to checkout.
  useEffect(() => {
    if (!user) return;
    let pending = null;
    try {
      pending = sessionStorage.getItem('pendingCheckoutVariant');
    } catch (_) { /* sessionStorage unavailable */ }
    if (!pending) return;
    try { sessionStorage.removeItem('pendingCheckoutVariant'); } catch (_) { /* no-op */ }

    (async () => {
      try {
        const response = await api.post('/api/payments/create-checkout', { variantName: pending });
        if (response.data?.checkoutUrl) {
          window.location.href = response.data.checkoutUrl;
        }
      } catch (error) {
        console.error('Failed to resume checkout after login:', error);
      }
    })();
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    refreshUser: checkLoggedIn, // Expose the function to refetch user data
    setUser, // Expose setUser to allow manual updates
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
